import React, { useState, useEffect } from 'react';
import AddInventoryModal from '../modals/AddInventoryModal';
import ServiceInventoryToolbar from '../toolbar/ServiceInventoryToolbar';
import useFetchServices from '../../hooks/InventoryHooks';
import { FaPlus } from "react-icons/fa";
import SubTypeComponent from "./SubTypeComponent";

import './Views.css';
import './ServiceInventory.css';

const Services = ({ user }) => {
	const { services, setServices, setFilteredServices, loading } = useFetchServices(user);

	const [categories, setCategories] = useState([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [types, setTypes] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState("");
	const [newTypeTitle, setNewTypeTitle] = useState("");
	const [isAddingType, setIsAddingType] = useState(false);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/service-categories-inventory/${user.account_id}`);
				if (!response.ok) {
					throw new Error('Network response was not ok');
				}
				const data = await response.json();
				setCategories(data);
			} catch (error) {
				console.error('Error fetching categories', error);
			}
		};

		fetchCategories();
	}, [user]);

	useEffect(() => {
		const fetchFilteredServices = async () => {
			try {
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/services_inventory/${user.account_id}`);
				if (!response.ok) {
					throw new Error('Network response was not ok');
				}
				const data = await response.json();
				setFilteredServices(data);
			} catch (error) {
				console.error('Error fetching services', error);
			}
		};

		fetchFilteredServices();
	}, [setFilteredServices, user]);

	const fetchTypes = async (categoryId) => {
		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/service-types/${categoryId}`);
			const responseData = await response.json();

			if (response.ok && Array.isArray(responseData.category?.typeTree)) {
				setTypes(responseData.category.typeTree); // Use the array returned from the backend
			} else {
				console.error("Expected typeTree as an array but received:", responseData);
				setTypes([]); // Reset to empty array if unexpected structure
			}
		} catch (error) {
			console.error("Error fetching types:", error);
			setTypes([]); // Reset to empty array on error
		}
	};

	const handleCategoryChange = (e) => {
		setSelectedCategory(e.target.value);
		setTypes([]);
		if (e.target.value) fetchTypes(e.target.value);
	};

	const handleStartAddingType = () => setIsAddingType(true);

	const handleSave = async (e, newTitle, parentTypeId = null) => {
		e.preventDefault();

		if (!newTitle.trim()) {
			console.error("Invalid title provided.");
			return;
		}

		try {
			const payload = {
				title: newTitle.trim(),
				parentTypeId,
				categoryId: selectedCategory,
			};

			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/services`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			});

			if (response.ok) {
				fetchTypes(selectedCategory);
				setNewTypeTitle("");
				setIsAddingType(false);
			} else {
				const errorData = await response.json();
				console.error("Error adding new type:", errorData);
			}
		} catch (error) {
			console.error("Error adding new type:", error);
		}
	};

	const handleEdit = async (e, typeId, newTitle, price) => {
		e.preventDefault();

		if (!typeId || !newTitle.trim()) {
			console.error("Invalid input for editing.");
			return;
		}

		try {
			const payload = {
				title: newTitle.trim(),
				price: price !== undefined ? parseFloat(price) : undefined,
			};

			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/services/${typeId}`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			});

			if (response.ok) {
				fetchTypes(selectedCategory);
			} else {
				const errorData = await response.json();
				console.error("Error editing type:", errorData);
			}
		} catch (error) {
			console.error("Error editing type:", error);
		}
	};

	const handleDelete = async (typeId) => {
		if (!typeId) {
			console.error("Invalid typeId provided.");
			return;
		}

		try {
			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/services/${typeId}`, {
				method: "DELETE",
			});

			if (response.ok) {
				fetchTypes(selectedCategory);
			} else {
				const errorData = await response.json();
				console.error("Error deleting type:", errorData);
			}
		} catch (error) {
			console.error("Error deleting type:", error);
		}
	};

	return (
		<div>
			<ServiceInventoryToolbar services={services} setFilteredServices={setFilteredServices} setServices={setServices} user={user} onOpenModal={() => setIsModalOpen(true)} />

			{isModalOpen && <AddInventoryModal handleCloseModal={() => setIsModalOpen(false)} user={user} onAddCategory={(newCategory) => setCategories((prev) => [...prev, newCategory])} />}

			{loading ? (
				<div className="loader"></div>
			) : (
				<div className="serviceinventorycontainer">
					<div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
						<label htmlFor="category-select" className="text-sm font-semibold text-gray-800">
							Select Category
						</label>
						<select id="category-select" value={selectedCategory} onChange={handleCategoryChange} className="select">
							<option value="">Select a category</option>
							{categories.map((category) => (
								<option key={category.id} value={category.id}>
									{category.title}
								</option>
							))}
						</select>
						{selectedCategory && !isAddingType && (
							<button onClick={handleStartAddingType} className="button button-blue">
								Add New Type <FaPlus />
							</button>
						)}
					</div>

					{isAddingType && (
						<div className="box">
							<input
								type="text"
								value={newTypeTitle}
								onChange={(e) => setNewTypeTitle(e.target.value)}
								placeholder="Enter new type title"
								className="input"
							/>
							<div style={{ display: 'flex', flexDirection: 'row', gap: '2vh', justifyContent: 'flex-end' }}>
								<button onClick={() => setIsAddingType(false)} className="button button-gray">
									Cancel
								</button>
								<button onClick={(e) => handleSave(e, newTypeTitle)} className="button button-blue">
									Save
								</button>
							</div>
						</div>
					)}

					{types.length === 0 && (
						<div className="text-gray-500">No types available for this category.</div>
					)}

					<div>
						<SubTypeComponent types={Array.isArray(types) ? types : []} depth={1} onSave={handleSave} onEdit={handleEdit} onDelete={handleDelete} />
					</div>
				</div>
			)}
		</div>
	);
};

export default Services;
